import React from 'react';
import { fetchQuery, graphql } from 'relay-runtime';
import type { AuthProvider } from '@atlaskit/media-core';
import type { Auth } from '@atlaskit/media-core/auth';
import batch from '@atlassian/help-center-common-util/batch-function';
import { getCloudId, getHelpCenterAri } from '@helpcenter/meta';
import environment from '@atlassian/help-center-common-util/relay/environment';
import { sentryQueue } from '@atlassian/help-center-common-util/sentry-queue';
import type { helpCenterAuthProviderQuery } from './__generated__/helpCenterAuthProviderQuery.graphql';

const MEDIA_TOKEN_EXPIRY_DURATION_IN_MS = 1000 * 60 * 10; // 10 minutes
const fetchMediaConfig = async () => {
    try {
        const fetchedData = await fetchQuery<helpCenterAuthProviderQuery>(
            environment,
            graphql`
                query helpCenterAuthProviderQuery($helpCenterAri: ID!, $cloudId: ID!) {
                    helpLayout(cloudId: $cloudId) @optIn(to: "HelpLayoutExperimentalSchema") {
                        mediaConfig(parentAri: $helpCenterAri) @required(action: THROW) {
                            mediaToken @required(action: THROW)
                            mediaUrl @required(action: THROW)
                            asapIssuer @required(action: THROW)
                        }
                    }
                }
            `,
            {
                helpCenterAri: getHelpCenterAri(),
                cloudId: getCloudId(),
            },
            { fetchPolicy: 'network-only' } // always get fresh tokens
        ).toPromise();
        const mediaConfig = fetchedData?.helpLayout?.mediaConfig;
        if (mediaConfig) {
            return {
                token: mediaConfig.mediaToken,
                baseUrl: mediaConfig.mediaUrl,
                asapIssuer: mediaConfig.asapIssuer,
            };
        }
    } catch (err) {
        sentryQueue.pushError(err);
    }
    return null;
};
const fetchBatchedMediaConfig = batch(fetchMediaConfig, 0);

export const useGetValidHelpCenterAuthProvider = (
    inputAuth: Auth,
    tokenExpiryDuration = MEDIA_TOKEN_EXPIRY_DURATION_IN_MS
): AuthProvider => {
    const mediaCredentials = React.useRef({
        auth: inputAuth,
        generationTime: Date.now(),
    });
    React.useEffect(() => {
        // reset the token generation time when the token is updated
        mediaCredentials.current = {
            auth: inputAuth,
            generationTime: Date.now(),
        };
        // only keeping token string as an dependency to avoid unnecessary update of generationTime
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [inputAuth.token]);

    return React.useCallback(async () => {
        return new Promise<Auth>((resolve) => {
            if (mediaCredentials.current.generationTime + tokenExpiryDuration > Date.now()) {
                // token is not expired yet
                resolve(mediaCredentials.current.auth);
            } else {
                // token is expired
                fetchBatchedMediaConfig()
                    .then((mediaConfig) => {
                        if (mediaConfig) {
                            mediaCredentials.current = {
                                auth: mediaConfig,
                                generationTime: Date.now(),
                            };
                            resolve(mediaConfig);
                        } else {
                            resolve(mediaCredentials.current.auth);
                        }
                    })
                    .catch(() => {
                        resolve(mediaCredentials.current.auth);
                    });
            }
        });
    }, [tokenExpiryDuration]);
};
